import { find } from '@go-client/lib/api/reservable';
import { ReservableEventTemplate } from '@go-shared/types/reservable';
import React, { useEffect, useState } from 'react';
import { BubbleButton, BubbleCheckboxGroup, BubbleOverlay } from './BubbleUI';
import BubbleHintSelect from './BubbleUI/BubbleHintSelect';
import { Popup } from '@spark-labs/avocado-ui';

interface ReserveCopyFromExperienceProps<T> {
  title: string;
  propertyId: string;
  selector: (experience: ReservableEventTemplate) => T[];
  onCopy: (items: T[]) => void;
  onClose: () => void;
}

export default function ReserveCopyFromExperience<T extends { _id: string; name: string }>(
  props: ReserveCopyFromExperienceProps<T>
) {
  const { title, propertyId, selector, onCopy, onClose } = props;

  const [experiences, setExperiences] = useState<ReservableEventTemplate[]>([]);
  const [selectedExperience, setSelectedExperience] = useState<ReservableEventTemplate | undefined>(
    undefined
  );
  const [selectedItems, setSelectedItems] = useState<T[]>([]);
  const [loading, setLoading] = useState(true);

  async function fetchData() {
    try {
      const experiences = await find(propertyId);
      setExperiences(experiences);
    } finally {
      setLoading(false);
    }
  }

  useEffect(() => {
    fetchData().catch(console.error);
  }, []);

  function handleCopy() {
    onCopy(selectedItems);
    onClose();
  }

  const availableItems = selectedExperience ? selector(selectedExperience) : [];

  return (
    <Popup title={`Experience ${title} Importer`} onClose={onClose}>
      <div className="px-3 py-2 grid grid-cols-1 gap-4" style={{ minWidth: '400px' }}>
        <BubbleOverlay loading={loading} loadingMinHeight="small">
          <BubbleHintSelect
            block
            allowUndefined
            options={experiences}
            keyExtractor={(e: ReservableEventTemplate) => e._id}
            labelExtractor={(e: ReservableEventTemplate) => e.title}
            value={selectedExperience}
            onValue={(e?: ReservableEventTemplate) => {
              setSelectedExperience(e);
              setSelectedItems([]);
            }}
            placeholder="Select an experience..."
          />
          {selectedExperience && (
            <div className="mt-3 space-y-2">
              {availableItems.length === 0 ? (
                <p className="text-sm text-gray-400">No items available in this experience.</p>
              ) : (
                <BubbleCheckboxGroup<T>
                  vertical
                  options={availableItems}
                  labelExtractor={i => i.name}
                  value={selectedItems}
                  onValue={setSelectedItems}
                  isSingleMode={false}
                />
              )}
            </div>
          )}
        </BubbleOverlay>
        <div className="flex items-center">
          <div></div>
          <div className="ml-auto"></div>
          <BubbleButton variant="navy" disabled={selectedItems.length === 0} onClick={handleCopy}>
            {selectedItems.length > 0
              ? `Copy ${selectedItems.length} item${selectedItems.length > 1 ? 's' : ''}`
              : 'Copy items'}
          </BubbleButton>
        </div>
      </div>
    </Popup>
  );
}
